import { useParams } from 'react-router';
import { FormikErrors, useFormik } from 'formik';
import { format, parse } from 'date-fns';
import {
  CreateFollowupsConfigDto,
  CreatePositionSequencePreferencesRequest,
  CreateSenderDto,
  CreatScheduleConfigDto,
} from '../../../../store/rtk-query/apis/sequence/dto/request/create-position-sequence-preferences.request';
import { SequenceScheduleModeEnum } from '../../../../store/rtk-query/apis/sequence/enums/sequence-schedule-days-mode.enum';
import { SequenceScheduleDaysModeEnum } from '../../../../store/rtk-query/apis/sequence/enums/sequence-schedule-mode.enum';
import { SequencePreferencesDto } from '../../../../store/rtk-query/apis/sequence/dto/sequence-preferences.dto';
import { SequenceFollowupsModeEnum } from '../../../../store/rtk-query/apis/sequence/enums/sequence-followups-mode.enum';
import { SequenceModeEnum } from '../../../../store/rtk-query/apis/sequence/enums/sequence-mode.enum';
import {
  SCHEDULE_TIME_FORMAT,
} from '../../components/outreach/campaign-page-auto-mode-base-option/campaign-page-auto-mode-send-time-section/campaign-page-auto-mode-send-time-section.consts';
import {
  CreateOrUpdatePositionSequencePreferencesQueryArguments,
} from '../../../../store/rtk-query/apis/sequence/dto/query-arguments/create-or-update-position-sequence-preferences.query-arguments';
import { OutreachSequencePreferencesFormikKeys } from './campaign.enums';

export type OutreachSequencePreferencesFormikValues = {
  [OutreachSequencePreferencesFormikKeys.Mode]: SequenceModeEnum,
  [OutreachSequencePreferencesFormikKeys.PrimarySenderId]: string | undefined,
  [OutreachSequencePreferencesFormikKeys.ScheduleMode]: SequenceScheduleModeEnum,
  [OutreachSequencePreferencesFormikKeys.ScheduleDaysType]: SequenceScheduleDaysModeEnum | undefined,
  [OutreachSequencePreferencesFormikKeys.ScheduleTime]: Date | undefined,
  [OutreachSequencePreferencesFormikKeys.FollowupsMode]: SequenceFollowupsModeEnum,
  [OutreachSequencePreferencesFormikKeys.FollowupsNumber]: number | undefined,
};

export type OutreachContentPreferencesFormik = OutreachSequencePreferencesFormikValues & {
  setValueByKey: (key: OutreachSequencePreferencesFormikKeys, value: unknown) => void,
  save: () => Promise<void>;
  dirty: boolean,
  isValid: boolean,
  errors: FormikErrors<OutreachSequencePreferencesFormikValues>,
};

const generateInitialValue = (preferences?: SequencePreferencesDto, sequenceMode?: SequenceModeEnum) => {
  const primarySender = preferences?.senders?.find((sender) => sender.isPrimarySender);

  return {
    [OutreachSequencePreferencesFormikKeys.Mode]: sequenceMode || preferences?.mode || SequenceModeEnum.Manual,
    [OutreachSequencePreferencesFormikKeys.PrimarySenderId]: primarySender?.user?.id || undefined,
    [OutreachSequencePreferencesFormikKeys.ScheduleMode]: preferences?.schedule?.mode || SequenceScheduleModeEnum.Auto,
    [OutreachSequencePreferencesFormikKeys.ScheduleDaysType]: preferences?.schedule?.days || undefined,
    [OutreachSequencePreferencesFormikKeys.ScheduleTime]: preferences?.schedule?.time ? parse(preferences?.schedule?.time, SCHEDULE_TIME_FORMAT, new Date()) : undefined,
    [OutreachSequencePreferencesFormikKeys.FollowupsMode]: preferences?.followups?.mode || SequenceFollowupsModeEnum.Auto,
    [OutreachSequencePreferencesFormikKeys.FollowupsNumber]: preferences?.followups?.numOfFollowups || undefined,
  };
};

const getSenders = (values: Partial<OutreachSequencePreferencesFormikValues>): CreateSenderDto[] | undefined => {
  const senderDto: CreateSenderDto = {} as CreateSenderDto;
  const primarySenderId = values[OutreachSequencePreferencesFormikKeys.PrimarySenderId];

  if (primarySenderId) {
    senderDto.userId = primarySenderId;
    senderDto.isPrimarySender = true;

    return [senderDto];
  }

  return undefined;
};

const getScheduleConfig = (values: Partial<OutreachSequencePreferencesFormikValues>): CreatScheduleConfigDto | undefined => {
  const scheduleConfigDto: CreatScheduleConfigDto = {} as CreatScheduleConfigDto;
  const isCustomMode = values[OutreachSequencePreferencesFormikKeys.ScheduleMode] === SequenceScheduleModeEnum.Custom;

  if (values[OutreachSequencePreferencesFormikKeys.ScheduleMode]) {
    scheduleConfigDto.mode = values[OutreachSequencePreferencesFormikKeys.ScheduleMode];
  }

  if (isCustomMode) {
    if (values[OutreachSequencePreferencesFormikKeys.ScheduleDaysType]) {
      scheduleConfigDto.days = values[OutreachSequencePreferencesFormikKeys.ScheduleDaysType];
    }

    if (values[OutreachSequencePreferencesFormikKeys.ScheduleTime]) {
      const c = values[OutreachSequencePreferencesFormikKeys.ScheduleTime] as unknown as Date;
      scheduleConfigDto.time = format(c, SCHEDULE_TIME_FORMAT);
    }
  }

  if (Object.keys(scheduleConfigDto).length) {
    return scheduleConfigDto;
  }

  return undefined;
};

const getFollowupsConfig = (values: Partial<OutreachSequencePreferencesFormikValues>): CreateFollowupsConfigDto | undefined => {
  const followupsConfigDto: CreateFollowupsConfigDto = {} as CreateFollowupsConfigDto;
  const isCustomMode = values[OutreachSequencePreferencesFormikKeys.FollowupsMode] === SequenceFollowupsModeEnum.Custom;

  if (values[OutreachSequencePreferencesFormikKeys.FollowupsMode]) {
    followupsConfigDto.mode = values[OutreachSequencePreferencesFormikKeys.FollowupsMode];
  }

  if (isCustomMode) {
    if (values[OutreachSequencePreferencesFormikKeys.FollowupsNumber]) {
      followupsConfigDto.numOfFollowups = values[OutreachSequencePreferencesFormikKeys.FollowupsNumber];
    }
  }

  if (Object.keys(followupsConfigDto).length) {
    return followupsConfigDto;
  }

  return undefined;
};

const getRequestData = (changedValues: Partial<OutreachSequencePreferencesFormikValues>) => {
  const data: CreatePositionSequencePreferencesRequest = {
    sequencePreferences: {
      mode: changedValues.mode || undefined,
    },
  };

  if (changedValues.mode === SequenceModeEnum.Auto) {
    data.sequencePreferences = {
      ...data.sequencePreferences,
      senders: getSenders(changedValues),
      schedule: getScheduleConfig(changedValues),
      followups: getFollowupsConfig(changedValues),
    };
  }

  return data;
};

export const useOutreachSequencePreferencesFormik = (
  submitHandler: (positionSequencePreferences: CreateOrUpdatePositionSequencePreferencesQueryArguments) => void,
  sequencePreferencesDto?: SequencePreferencesDto,
  sequenceMode?: SequenceModeEnum,
): OutreachContentPreferencesFormik => {
  const { positionId } = useParams<MatchUrlParams>();
  const initialValues = generateInitialValue(sequencePreferencesDto, sequenceMode);
  const formik = useFormik<OutreachSequencePreferencesFormikValues>({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values: OutreachSequencePreferencesFormikValues) => {
      const requestArguments = {
        positionId,
        data: getRequestData(values),
      };

      return submitHandler(requestArguments);
    },
    validate: (values: OutreachSequencePreferencesFormikValues) => {
      const errors: OutreachSequencePreferencesFormikValues = {} as OutreachSequencePreferencesFormikValues;
      const isAutoMode = values[OutreachSequencePreferencesFormikKeys.Mode] === SequenceModeEnum.Auto;
      if (isAutoMode && !values[OutreachSequencePreferencesFormikKeys.PrimarySenderId]) {
        errors[OutreachSequencePreferencesFormikKeys.PrimarySenderId] = 'Primary sender id cannot be null';
      }

      return errors;
    },
  });

  return {
    [OutreachSequencePreferencesFormikKeys.Mode]: formik.values[OutreachSequencePreferencesFormikKeys.Mode],
    [OutreachSequencePreferencesFormikKeys.PrimarySenderId]: formik.values[OutreachSequencePreferencesFormikKeys.PrimarySenderId],
    [OutreachSequencePreferencesFormikKeys.ScheduleMode]: formik.values[OutreachSequencePreferencesFormikKeys.ScheduleMode],
    [OutreachSequencePreferencesFormikKeys.ScheduleDaysType]: formik.values[OutreachSequencePreferencesFormikKeys.ScheduleDaysType],
    [OutreachSequencePreferencesFormikKeys.ScheduleTime]: formik.values[OutreachSequencePreferencesFormikKeys.ScheduleTime],
    [OutreachSequencePreferencesFormikKeys.FollowupsMode]: formik.values[OutreachSequencePreferencesFormikKeys.FollowupsMode],
    [OutreachSequencePreferencesFormikKeys.FollowupsNumber]: formik.values[OutreachSequencePreferencesFormikKeys.FollowupsNumber],
    setValueByKey: (key, value) => {
      formik.setFieldValue(key, value);
    },
    save: formik.submitForm,
    dirty: formik.dirty,
    errors: formik.errors,
    isValid: formik.isValid,
  };
};
